import { type FC } from 'react';
import { LogOut } from 'lucide-react';
import { Sheet } from '../primitives/Sheet';
import { ACCLogo, type Product } from './ACCLogo';
import { cn } from '@/lib/utils';

/**
 * HamburgerSheet — right-side sheet with profile, product switcher,
 * notifications, and account links. Design spec: ACC Agentic Dashboard
 * spec, section 6.
 *
 * The sheet is intentionally opinionated so every ACC Agentic product
 * shows the same structure. Product-specific behaviour (e.g. which
 * notifications show) flows through props, not component changes.
 */

export interface HamburgerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    displayName: string;
    email: string;
    initials: string;
  };
  currentProduct: Product;
  onSwitchProduct?: (product: Product) => void;
  onSignOut: () => void;
}

const SectionLabel: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="pt-5 pb-2 text-micro font-semibold tracking-[0.08em] uppercase text-text-muted">
    {children}
  </div>
);

const ProductTile: FC<{
  product: Product;
  label: string;
  active: boolean;
  onClick?: () => void;
}> = ({ product, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center gap-2.5 p-2.5 rounded-glass-sm border transition-colors text-left',
      active
        ? 'bg-brand-blue/10 border-brand-blue/35'
        : 'bg-white/50 border-glass-hairline hover:bg-white/80',
    )}
  >
    <ACCLogo product={product} size={28} />
    <span className="text-caption font-semibold text-text-primary truncate">{label}</span>
  </button>
);

const AccountLink: FC<{
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick?: () => void;
}> = ({ icon, label, danger = false, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-glass-sm hover:bg-slate-900/5 transition-colors min-h-10',
      danger ? 'text-red-700' : 'text-text-secondary',
    )}
  >
    <span className={cn('w-5 h-5 flex items-center justify-center', danger && 'text-red-700')}>
      {icon}
    </span>
    <span className="text-label font-medium">{label}</span>
  </button>
);

export const HamburgerSheet: FC<HamburgerSheetProps> = ({
  open,
  onOpenChange,
  user,
  currentProduct,
  onSwitchProduct,
  onSignOut,
}) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} side="right" title="Menu" widthPx={340}>
      {/* Profile block */}
      <div className="flex items-center gap-3">
        <div
          className="w-[42px] h-[42px] rounded-full flex items-center justify-center text-white font-semibold text-body"
          style={{
            background: 'linear-gradient(135deg, #1e40af, #172554)',
            boxShadow: '0 6px 16px rgba(30, 64, 175, 0.35), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}
          aria-hidden
        >
          {user.initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-body font-semibold text-text-primary truncate">
            {user.displayName}
          </div>
          <div className="text-caption text-text-tertiary truncate">{user.email}</div>
        </div>
      </div>

      <SectionLabel>Switch product</SectionLabel>
      <div className="grid grid-cols-2 gap-2">
        <ProductTile
          product="loanaudit"
          label="Loan Audit"
          active={currentProduct === 'loanaudit'}
          onClick={() => onSwitchProduct?.('loanaudit')}
        />
        <ProductTile
          product="humans"
          label="Agentic Humans"
          active={currentProduct === 'humans'}
          onClick={() => onSwitchProduct?.('humans')}
        />
        <ProductTile
          product="compliance"
          label="Compliance"
          active={currentProduct === 'compliance'}
          onClick={() => onSwitchProduct?.('compliance')}
        />
        <ProductTile
          product="insurance"
          label="Insurance UW"
          active={currentProduct === 'insurance'}
          onClick={() => onSwitchProduct?.('insurance')}
        />
        <ProductTile
          product="brand"
          label="Brand Mgmt"
          active={currentProduct === 'brand'}
          onClick={() => onSwitchProduct?.('brand')}
        />
      </div>

      <SectionLabel>Account</SectionLabel>
      <div className="flex flex-col">
        <AccountLink
          icon={<LogOut className="w-4 h-4" />}
          label="Sign out"
          danger
          onClick={onSignOut}
        />
      </div>
    </Sheet>
  );
};

export default HamburgerSheet;
